import { useCallback, useEffect, useRef } from 'react';
import {
    unstable_useBlocker,
    type Location,
    type unstable_BlockerFunction as BlockerFunction,
} from 'react-router-dom';

function pathnameAndHashSame(
    a: Pick<Location, 'pathname' | 'hash'>,
    b: Pick<Location, 'pathname' | 'hash'>,
): boolean {
    return a.pathname === b.pathname && a.hash === b.hash;
}

function isOpeningOrSwitchingRequestDrawerSearch(
    currentLocation: Pick<Location, 'pathname' | 'search' | 'hash'>,
    nextLocation: Pick<Location, 'pathname' | 'search' | 'hash'>,
): boolean {
    if (!pathnameAndHashSame(currentLocation, nextLocation)) {
        return false;
    }
    const current = new URLSearchParams(currentLocation.search);
    const next = new URLSearchParams(nextLocation.search);
    const requestIdentifier = next.get('requestId');
    if (!requestIdentifier) {
        return false;
    }

    const keys = new Set([...current.keys(), ...next.keys()]);
    return Array.from(keys).every(key => {
        if (key === 'requestId') {
            return true;
        }
        const currentValues = current.getAll(key);
        const nextValues = next.getAll(key);
        return (
            currentValues.length === nextValues.length &&
            currentValues.every((value, index) => value === nextValues[index])
        );
    });
}

function isClosingRequestDrawerSearch(
    currentLocation: Pick<Location, 'pathname' | 'search' | 'hash'>,
    nextLocation: Pick<Location, 'pathname' | 'search' | 'hash'>,
): boolean {
    if (!pathnameAndHashSame(currentLocation, nextLocation)) {
        return false;
    }
    const cur = new URLSearchParams(currentLocation.search);
    const nxt = new URLSearchParams(nextLocation.search);

    if (!cur.get('requestId')) {
        return false;
    }
    if (nxt.get('requestId')) {
        return false;
    }

    const nextKeysMatchCurrent = Array.from(nxt.keys()).every(key => {
        const nextVals = nxt.getAll(key);
        const curVals = cur.getAll(key);
        return (
            nextVals.length === curVals.length &&
            nextVals.every((v, i) => v === curVals[i])
        );
    });
    if (!nextKeysMatchCurrent) {
        return false;
    }

    const curDistinctKeys = new Set(cur.keys());
    const nxtDistinctKeys = new Set(nxt.keys());
    const hasExtraCurrentOnlyKey = Array.from(curDistinctKeys).some(
        key => !nxtDistinctKeys.has(key) && key !== 'requestId',
    );
    if (hasExtraCurrentOnlyKey) {
        return false;
    }

    return true;
}

export function useUnsavedChangesPrompt(isDirty: boolean) {
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const shouldBlockNavigation = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) => {
            if (!isDirtyRef.current) {
                return false;
            }

            if (
                isOpeningOrSwitchingRequestDrawerSearch(
                    currentLocation,
                    nextLocation,
                ) ||
                isClosingRequestDrawerSearch(currentLocation, nextLocation)
            ) {
                return false;
            }

            return true;
        },
        [],
    );

    const blocker = unstable_useBlocker(shouldBlockNavigation);

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = true;
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    return blocker;
}
